import { lazy, memo, Suspense, useEffect } from 'react';
import type { FC } from 'react';

import classes from './App.module.css';
import resets from './components/_resets.module.css';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Login } from "./pages/Login"
import { RoomArrayProvider } from './context/RoomArrayContext';
import { ProtectedRoute } from "./components/ProtectedRoute";
import { canMutate } from "./components/_shared/permissions";
import { ConfirmDialogHost } from './components/_shared/confirmService';
import { usingFallbackTenantConfig } from "./config";
import { FACILITIES_TAB_ENABLED } from "./utils/featureFlags";
import toast, { Toaster, useToasterStore } from 'react-hot-toast';

// Pages are code-split so each route's JS loads on first visit instead of
// riding in the main bundle. Login stays eager: it's the entry page.
// The importers live in one list so the app can PREFETCH every chunk during
// idle time after mount (Kiara 8/25: the first click on Rooms/Charts felt
// like a full page refresh — that was the chunk downloading behind a blank
// fallback). Prefetching costs a little idle bandwidth once and nothing else;
// after it, every first click renders instantly from cache.
const AllRooms = lazy(() => import("./components/AllRooms/AllRooms").then(m => ({ default: m.AllRooms })));
const RoomEdit = lazy(() => import("./components/RoomEdit/RoomEdit").then(m => ({ default: m.RoomEdit })));
const AgentsCatalog = lazy(() => import("./components/AgentsCatalog/AgentsCatalog").then(m => ({ default: m.AgentsCatalog })));
const RoomLayout = lazy(() => import("./layouts/RoomLayout").then(m => ({ default: m.RoomLayout })));
const EquipmentLandingPage = lazy(() => import("./components/Equipments/EquipmentLandingPage").then(m => ({ default: m.EquipmentLandingPage })));
const AuditLog = lazy(() => import("./components/AuditLog/AuditLog").then(m => ({ default: m.AuditLog })));
const Charts = lazy(() => import("./components/Charts/Charts").then(m => ({ default: m.Charts })));
const NewUser = lazy(() => import("./components/NewUser/NewUser").then(m => ({ default: m.NewUser })));
const UserManagement = lazy(() => import("./components/UserManagement/UserManagement").then(m => ({ default: m.UserManagement })));
const UserEdit = lazy(() => import("./components/UserEdit/UserEdit").then(m => ({ default: m.UserEdit })));
const Dashboard = lazy(() => import("./pages/Dashboard").then(m => ({ default: m.Dashboard })));
const Settings = lazy(() => import("./components/Settings/Settings").then(m => ({ default: m.Settings })));
const CommentReasons = lazy(() => import("./components/CommentReasons/CommentReasons").then(m => ({ default: m.CommentReasons })));
const Vendors = lazy(() => import("./components/Vendors/Vendors").then(m => ({ default: m.Vendors })));
const Facilities = lazy(() => import("./components/Facilities/Facilities").then(m => ({ default: m.Facilities })));
const Notifications = lazy(() => import("./components/Notifications/Notifications").then(m => ({ default: m.Notifications })));
const ResetPassword = lazy(() => import("./components/ResetPassword/ResetPassword").then(m => ({ default: m.ResetPassword })));
const Transactions = lazy(() => import("./components/Transactions/Transactions").then(m => ({ default: m.Transactions })));

const CHUNK_PREFETCHERS: Array<() => Promise<unknown>> = [
  () => import("./components/AllRooms/AllRooms"),
  () => import("./components/RoomEdit/RoomEdit"),
  () => import("./components/AgentsCatalog/AgentsCatalog"),
  () => import("./layouts/RoomLayout"),
  () => import("./components/Equipments/EquipmentLandingPage"),
  () => import("./components/AuditLog/AuditLog"),
  () => import("./components/Charts/Charts"),
  () => import("./components/NewUser/NewUser"),
  () => import("./components/UserManagement/UserManagement"),
  () => import("./components/UserEdit/UserEdit"),
  () => import("./pages/Dashboard"),
  () => import("./components/Settings/Settings"),
  () => import("./components/CommentReasons/CommentReasons"),
  () => import("./components/Vendors/Vendors"),
  () => import("./components/Notifications/Notifications"),
  () => import("./components/Transactions/Transactions"),
];

/** Warm every route chunk once the browser is idle. Failures are ignored —
 *  the route still lazy-loads on demand exactly as before. */
function usePrefetchRouteChunks() {
  useEffect(() => {
    const kick = () => CHUNK_PREFETCHERS.forEach(f => { f().catch(() => {}); });
    const w = window as any;
    const handle = typeof w.requestIdleCallback === 'function'
      ? w.requestIdleCallback(kick, { timeout: 4000 })
      : setTimeout(kick, 1500);
    return () => {
      if (typeof w.cancelIdleCallback === 'function') w.cancelIdleCallback(handle);
      else clearTimeout(handle);
    };
  }, []);
}

/** WK826-MODAL-01: spamming saves/deletes piled toasts down the screen until
 *  each expired. react-hot-toast has no built-in cap, so dismiss everything
 *  beyond the newest three — the stack stays readable under rapid actions. */
const MAX_VISIBLE_TOASTS = 3;
function ToastLimiter() {
  const { toasts } = useToasterStore();
  useEffect(() => {
    toasts
      .filter(t => t.visible)
      .filter((_, i) => i >= MAX_VISIBLE_TOASTS)
      .forEach(t => toast.dismiss(t.id));
  }, [toasts]);
  return null;
}

function RouteFallback() {
  return (
    <div style={{
      position: 'fixed', inset: 0, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      color: '#606e7d', fontSize: 14,
    }}>
      Loading…
    </div>
  );
}

interface Props {
  className?: string;
}
export const App: FC<Props> = memo(function App(props = {}) {
  usePrefetchRouteChunks();
  return (
    <div className={`${resets.clapyResets} ${classes.root}`}>
      {/* Loud, visible warning when a production build is missing its
          tenant env vars and has silently fallen back to the SixSigma defaults. */}
      {process.env.NODE_ENV === 'production' && usingFallbackTenantConfig && (
        <div
          role="alert"
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
            background: '#b45309', color: '#fff', textAlign: 'center',
            padding: '6px 12px', fontSize: 13, fontWeight: 600,
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          Configuration warning: tenant environment variables are not set — using SixSigma defaults.
        </div>
      )}
      <ToastLimiter />
      <Toaster
        position="top-right"
        toastOptions={{
          success: { style: { borderLeft: '4px solid #00CDC7' }, duration: 3000 },
          error: { style: { borderLeft: '4px solid #e74c3c' }, duration: 5000 },
        }}
      />
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
        {/* Authenticated routes wrapped in <ProtectedRoute> so an
            expired/absent session redirects to /login instead of rendering a
            page against an empty company id. /login and /reset-password stay
            public. */}
        {/* WK826-AUDGATE-01 (b1217): the create-room wizard is a pure
            mutation flow — an Auditor typing the URL directly lands back on
            the read-only Rooms list instead. */}
        <Route path="create-room" element={
              <ProtectedRoute>
                {canMutate() ? (
                  <RoomArrayProvider>
                    <RoomLayout />
                  </RoomArrayProvider>
                ) : (
                  <Navigate to="/rooms" replace />
                )}
              </ProtectedRoute>
            }>
            </Route>
          {/* One catalog page, three modes; distinct keys force a remount when
              switching routes so state never leaks between modes. */}
          <Route path="cleaning-agents" element={<ProtectedRoute><AgentsCatalog key="cleaning" mode="cleaning" /></ProtectedRoute>}>
          </Route>
          <Route path="disinfecting" element={<ProtectedRoute><AgentsCatalog key="disinfecting" mode="disinfecting" /></ProtectedRoute>}>
          </Route>
          {/* Combined Cleaning & Disinfecting module (flag-gated sidebar entry;
              the route itself is additive and harmless when the flag is off). */}
          <Route path="agents" element={<ProtectedRoute><AgentsCatalog key="combined" mode="combined" /></ProtectedRoute>}>
          </Route>
          <Route path="login" element={<Login />}>
          </Route>
          <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>}>
          </Route>
          <Route path="equipments" element={<ProtectedRoute><EquipmentLandingPage /></ProtectedRoute>}>
          </Route>
          <Route path="rooms" element={<ProtectedRoute><AllRooms /></ProtectedRoute>}>
          </Route>
          <Route path="room-edit/:roomId" element={<ProtectedRoute><RoomEdit /></ProtectedRoute>}>
          </Route>
          <Route path="audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>}>
          </Route>
          <Route path="charts" element={<ProtectedRoute><Charts /></ProtectedRoute>}>
          </Route>
          <Route path="new-user" element={<ProtectedRoute><NewUser /></ProtectedRoute>}>
          </Route>
          <Route path="users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>}>
          </Route>
          <Route path="user-edit" element={<ProtectedRoute><UserEdit /></ProtectedRoute>}>
          </Route>
          <Route path="settings" element={<ProtectedRoute><Settings /></ProtectedRoute>}>
          </Route>
          <Route path="settings/comment-reasons" element={<ProtectedRoute><CommentReasons /></ProtectedRoute>}>
          </Route>
          <Route path="vendors" element={<ProtectedRoute><Vendors /></ProtectedRoute>}>
          </Route>
          {/* FACILITIES_TAB_ENABLED-gated (QA 8/22 "hide for now"): with the
              switch off, bookmarks to /facilities bounce to the dashboard. */}
          <Route path="facilities" element={FACILITIES_TAB_ENABLED
            ? <ProtectedRoute><Facilities /></ProtectedRoute>
            : <Navigate to="/dashboard" replace />}>
          </Route>
          <Route path="notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>}>
          </Route>
          {/* Admin Transactions page (adminTransactionsEnabled-gated sidebar
              entry; the component itself redirects flag-off tenants and never
              polls for them, so the route is additive and inert). */}
          <Route path="transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>}>
          </Route>
          <Route path="reset-password" element={<ResetPassword />}>
          </Route>
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
        </Suspense>
        {/* One branded confirm host for the whole app — confirmDialog() from
            any handler renders here instead of a native browser popup. */}
        <ConfirmDialogHost />
      </BrowserRouter>
    </div>

  );
});
